import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AcademicCapIcon,
  ChartBarIcon,
  CalendarIcon,
  BookOpenIcon,
  UserGroupIcon,
  ClipboardDocumentListIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';
import type { Curriculum, CurriculumCourse } from '../types';

const quickActions = [
  {
    name: 'Yeni Müfredat',
    href: '/app/curricula',
    icon: AcademicCapIcon,
    color: 'bg-blue-500',
  },
  {
    name: 'Takvim',
    href: '/app/calendar',
    icon: CalendarIcon,
    color: 'bg-green-500',
  },
  {
    name: 'Hedefler',
    href: '/app/goals',
    icon: ClipboardDocumentListIcon,
    color: 'bg-purple-500',
  },
  {
    name: 'Odak Modu',
    href: '/app/focus',
    icon: BookOpenIcon,
    color: 'bg-yellow-500',
  },
];

export default function Dashboard() {
  const { currentUser } = useAuth();
  const [curricula, setCurricula] = useState<Curriculum[]>([]);
  const [courses, setCourses] = useState<CurriculumCourse[]>([]);

  useEffect(() => {
    if (!currentUser) return;
    const fetchData = async () => {
      const curriculaRef = collection(db, 'curricula');
      const coursesRef = collection(db, 'curriculumCourses');
      const curriculaQ = query(curriculaRef, where('uid', '==', currentUser.uid));
      const coursesQ = query(coursesRef, where('uid', '==', currentUser.uid));
      const [curriculaSnap, coursesSnap] = await Promise.all([
        getDocs(curriculaQ),
        getDocs(coursesQ),
      ]);
      setCurricula(curriculaSnap.docs.map(d => d.data() as Curriculum));
      setCourses(coursesSnap.docs.map(d => d.data() as CurriculumCourse));
    };
    fetchData();
  }, [currentUser]);

  if (!currentUser) return <div>Loading...</div>;

  return (
    <div className="space-y-8">
      {/* Welcome Section */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-soft p-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          L&D Paneline Hoş Geldiniz
        </h1>
        <p className="mt-2 text-gray-600 dark:text-gray-300">
          Müfredatlar oluşturun, çalışanlarınıza eğitim atayın ve ROI takibi yapın.
        </p>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {quickActions.map((action) => (
          <Link
            key={action.name}
            to={action.href}
            className="flex items-center p-4 bg-white dark:bg-gray-800 rounded-xl shadow-soft hover:shadow-lg transition-shadow"
          >
            <div className={`p-2 rounded-lg ${action.color}`}>
              <action.icon className="w-6 h-6 text-white" />
            </div>
            <span className="ml-3 text-sm font-medium text-gray-900 dark:text-white">
              {action.name}
            </span>
          </Link>
        ))}
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft p-6">
          <div className="flex items-center">
            <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
              <AcademicCapIcon className="w-6 h-6 text-blue-500" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Toplam Müfredat
              </p>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">
                {curricula.length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft p-6">
          <div className="flex items-center">
            <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30">
              <BookOpenIcon className="w-6 h-6 text-green-500" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Toplam Kurs
              </p>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">
                {courses.length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft p-6">
          <div className="flex items-center">
            <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30">
              <UserGroupIcon className="w-6 h-6 text-purple-500" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Atamalar
              </p>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">
                —
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft p-6">
          <div className="flex items-center">
            <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30">
              <ChartBarIcon className="w-6 h-6 text-amber-500" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                ROI Özeti
              </p>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">
                —
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Müfredat Özeti */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-soft p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Müfredatlar
        </h2>
        {curricula.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <AcademicCapIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>Henüz müfredat yok. İlk müfredatınızı oluşturmak için yukarıdaki linke tıklayın.</p>
            <Link to="/app/curricula" className="mt-4 inline-block btn-primary">
              Müfredatlar Sayfasına Git
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {curricula.slice(0, 6).map((cur) => (
              <Link
                key={cur.id}
                to={`/app/curricula/${cur.id}`}
                className="block p-4 bg-gray-50 dark:bg-gray-700 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
              >
                <h3 className="font-medium text-gray-900 dark:text-white truncate">
                  {cur.title}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {courses.filter(c => c.curriculumId === cur.id).length} kurs
                </p>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
} 